import React, { useState, useEffect } from "react";

const PercentageCalculator = () => {
  const [totalValue, setTotalValue] = useState(1000);
  const [percentage, setPercentage] = useState(15);
  const [numberOfParts, setNumberOfParts] = useState(1);
  const [calculationType, setCalculationType] = useState("find");
  const [roundUp, setRoundUp] = useState(false);
  const [operationType, setOperationType] = useState("add");
  const [result, setResult] = useState({
    totalValue: "0.00",
    percentage: "0.00",
    percentageAmount: "0.00",
    finalValue: "0.00",
    perPartTotal: "0.00",
    perPartPercentage: "0.00",
    perPartFinal: "0.00",
    calculationTypeText: "",
    savingTip: "",
  });

  // Percentage Calculation Function
  const calculatePercentage = () => {
    let value = parseFloat(totalValue);
    let percent = parseFloat(percentage);
    let parts = parseFloat(numberOfParts);

    if (isNaN(value) || value < 0 || isNaN(percent) || percent < 0 || isNaN(parts) || parts < 1) return;

    // Calculate percentage amount
    let percentageAmount = (value * percent) / 100;
    
    // Round up if selected
    if (roundUp) {
      percentageAmount = Math.ceil(percentageAmount);
    }
    
    // Calculate final value based on operation type
    let finalValue = value;
    if (operationType === "add") {
      finalValue = value + percentageAmount;
    } else if (operationType === "subtract") {
      finalValue = value - percentageAmount;
    } else {
      finalValue = percentageAmount; // just the percentage amount
    }
    
    // Calculate per part amounts
    const perPartTotal = value / parts;
    const perPartPercentage = percentageAmount / parts;
    const perPartFinal = finalValue / parts;

    let typeText = "";
    let savingTip = "";

    switch(calculationType) {
      case "markup":
        typeText = "Markup Calculation (20-50%)";
        savingTip = "Markup calculations help determine selling prices. Add your desired profit margin to the cost price.";
        break;
      case "find":
        typeText = "Find Percentage (Standard)";
        savingTip = "Finding what percentage of a number is useful for statistics, grades, and data analysis.";
        break;
      case "increase":
        typeText = "Percentage Increase";
        savingTip = "Percentage increase shows growth. Commonly used for salary hikes, inflation, and business growth metrics.";
        break;
      case "decrease":
        typeText = "Percentage Decrease";
        savingTip = "Percentage decrease shows reduction. Used for discounts, depreciation, and loss calculations.";
        break;
      case "custom":
        typeText = `Custom Calculation (${percent}%)`;
        savingTip = "Custom percentage calculation. Useful for any specific percentage-based computation.";
        break;
      default:
        typeText = "Standard Calculation";
        savingTip = "Percentages are used in everyday life for discounts, taxes, tips, grades, and statistics.";
    }

    setResult({
      totalValue: value.toFixed(2),
      percentage: percent.toFixed(2),
      percentageAmount: percentageAmount.toFixed(2),
      finalValue: finalValue.toFixed(2),
      perPartTotal: perPartTotal.toFixed(2),
      perPartPercentage: perPartPercentage.toFixed(2),
      perPartFinal: perPartFinal.toFixed(2),
      calculationTypeText: typeText,
      savingTip,
    });
  };

  useEffect(() => {
    calculatePercentage();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
          </div>
          <h1>Percentage Calculator</h1>
          <p>
            Calculate percentages, percentage increase/decrease, and split values for various applications
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Calculation Details
              </h2>

              {/* Total Value */}
              <div className="input-group">
                <label>Total Value</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={totalValue}
                    onChange={(e) => setTotalValue(e.target.value)}
                    className="calc-input"
                    placeholder="1000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500, 1000, 2000, 5000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setTotalValue(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculation Type */}
              <div className="input-group">
                <label>Calculation Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "find", label: "Find % (Standard)" },
                    { value: "increase", label: "% Increase" },
                    { value: "decrease", label: "% Decrease" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setCalculationType(type.value);
                        if (type.value === "find") {
                          setPercentage(15);
                          setOperationType("find");
                        } else if (type.value === "increase") {
                          setPercentage(20);
                          setOperationType("add");
                        } else if (type.value === "decrease") {
                          setPercentage(15);
                          setOperationType("subtract");
                        }
                      }}
                      className={`quick-select-btn ${calculationType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "markup", label: "Markup %" },
                    { value: "custom", label: "Custom %" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setCalculationType(type.value);
                        if (type.value === "markup") setPercentage(30);
                      }}
                      className={`quick-select-btn ${calculationType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Percentage */}
              <div className="input-group">
                <label>Percentage (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={percentage}
                    onChange={(e) => setPercentage(e.target.value)}
                    className="calc-input"
                    placeholder="15"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[10, 15, 25, 50].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setPercentage(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Number of Parts */}
              <div className="input-group">
                <label>Number of Parts</label>
                <div className="input-wrapper">
                  <span className="input-prefix">#</span>
                  <input
                    type="number"
                    value={numberOfParts}
                    onChange={(e) => setNumberOfParts(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 2, 4, 5].map((parts) => (
                    <button
                      key={parts}
                      onClick={() => setNumberOfParts(parts)}
                      className="quick-select-btn success"
                    >
                      {parts} {parts === 1 ? 'Part' : 'Parts'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Operation Type */}
              <div className="input-group">
                <label>Operation</label>
                <div className="quick-select-group">
                  {[
                    { value: "add", label: "Add %" },
                    { value: "subtract", label: "Subtract %" },
                    { value: "find", label: "Find % Only" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setOperationType(type.value)}
                      className={`quick-select-btn ${operationType === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Round Up */}
              <div className="input-group">
                <label>Round Result?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Round Up)" },
                    { value: false, label: "No (Exact)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setRoundUp(type.value)}
                      className={`quick-select-btn ${roundUp === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculatePercentage} className="calculate-btn">
                Calculate Percentage
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Percentage Results
              </h2>

              <div className="result-card">
                {/* Final Value - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Final Result</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.finalValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.calculationTypeText} at {result.percentage}%
                  </div>
                </div>

                {/* Per Part Final */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Per Part Value</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.perPartFinal).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Each part after calculation
                  </div>
                </div>

                {/* Percentage Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Percentage Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.percentageAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.percentage}% of total value
                  </div>
                </div>

                {/* Total Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Original Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Base value
                  </div>
                </div>

                {/* Per Part Percentage */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Per Part Percentage</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.perPartPercentage).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Percentage share per part
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Value Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Original Value</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.totalValue).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseFloat(result.finalValue) > 0 ? (parseFloat(result.totalValue) / Math.max(parseFloat(result.totalValue), parseFloat(result.finalValue))) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Percentage ({result.percentage}%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.percentageAmount).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.totalValue) > 0 ? (parseFloat(result.percentageAmount) / parseFloat(result.totalValue)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Per Part Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Per Part Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Base Value Per Part</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.perPartTotal) / parseFloat(result.perPartFinal)) * 100).toFixed(2)}% (₹{Number(result.perPartTotal).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseFloat(result.perPartFinal) > 0 ? (parseFloat(result.perPartTotal) / Math.max(parseFloat(result.perPartTotal), parseFloat(result.perPartFinal))) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Percentage Share Per Part</span>
                      <span className="breakdown-percentage">
                        {parseFloat(result.perPartTotal) > 0 ? ((parseFloat(result.perPartPercentage) / parseFloat(result.perPartTotal)) * 100).toFixed(2) : '0.00'}% (₹{Number(result.perPartPercentage).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.perPartTotal) > 0 ? (parseFloat(result.perPartPercentage) / parseFloat(result.perPartTotal)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Percentage Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Percentage Tips</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} Understanding percentages is essential for finance, shopping, and data analysis.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Percentages</h3>
              <p>
                Percentage means "per hundred" and is a way to express a number as a fraction of 100. It's denoted by the 
                symbol %. Percentages are used everywhere in daily life - from calculating discounts and taxes to understanding 
                statistics, grades, and financial growth. To find what percentage one number is of another, divide the first 
                number by the second and multiply by 100. For example, if you scored 45 out of 50, that's (45÷50)×100 = 90%. 
                Percentage increase shows growth: if your salary went from ₹50,000 to ₹60,000, that's a 20% increase 
                [(10,000÷50,000)×100]. Percentage decrease works similarly for reductions. In business, markup percentage 
                determines selling price from cost price. A 30% markup on ₹1000 means adding ₹300, making the selling price 
                ₹1300. Banks use percentages for interest rates - a 7% annual interest on ₹100,000 gives ₹7,000 interest. 
                Percentages help compare different quantities on a common scale. Understanding percentage calculations is 
                crucial for making informed financial decisions, analyzing data, and everyday shopping.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Percentage Calculation Formulas:</h4>
            <div className="formula-display">
              Percentage Amount = (Value × Percentage) / 100
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Find Percentage</div>
                <div className="formula-param-value">(Part / Whole) × 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Percentage Increase</div>
                <div className="formula-param-value">Original + (Original × %/100)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Percentage Decrease</div>
                <div className="formula-param-value">Original - (Original × %/100)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Percentage Change</div>
                <div className="formula-param-value">[(New - Old) / Old] × 100</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How do I convert a fraction to percentage?</h5>
              <p>
                Divide the numerator by denominator and multiply by 100. For example, 3/4 = 0.75 × 100 = 75%. Or multiply both parts by a number that makes the denominator 100.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between percentage and percentile?</h5>
              <p>
                Percentage is a proportion out of 100. Percentile indicates the value below which a given percentage of observations fall. 90th percentile means 90% of values are below it.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate percentage change?</h5>
              <p>
                Formula: [(New Value - Old Value) / Old Value] × 100. If positive, it's an increase; if negative, it's a decrease. Example: From 50 to 60 = [(60-50)/50]×100 = 20% increase.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can percentages add up to more than 100%?</h5>
              <p>
                Yes, when dealing with overlapping categories or growth over time. For example, if three items each grow 50%, the total growth isn't 150% - they're independent percentages.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate reverse percentage?</h5>
              <p>
                If you know the final value after a percentage change, divide by (1 + percentage/100) for increase, or (1 - percentage/100) for decrease. Example: ₹1200 after 20% increase = 1200/1.2 = ₹1000 original.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the easiest way to calculate 15% of a number?</h5>
              <p>
                Find 10% by moving decimal left one place, then find 5% (half of 10%), and add them. Example: 15% of 200 = 20 (10%) + 10 (5%) = 30. Or use (Number × 15) / 100.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PercentageCalculator;